import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { facebookAdCreativesService, FbCreative } from '@/services/facebook-ad-creatives.service';
import {
  RefreshCw,
  Plus,
  Search,
  Image as ImageIcon,
  Film,
  Layers,
  HelpCircle,
  ExternalLink,
  Play,
  AlertCircle,
} from 'lucide-react';

type KindFilter = 'ALL' | 'image' | 'video';

const KIND_LABELS: Record<FbCreative['media_kind'], string> = {
  image: 'Image',
  video: 'Video',
  carousel: 'Carousel',
  unknown: 'Unknown',
};

const KIND_ICON: Record<FbCreative['media_kind'], React.ElementType> = {
  image: ImageIcon,
  video: Film,
  carousel: Layers,
  unknown: HelpCircle,
};

const CTA_LABEL: Record<string, string> = {
  LEARN_MORE: 'Learn More',
  SHOP_NOW: 'Shop Now',
  SIGN_UP: 'Sign Up',
  CONTACT_US: 'Contact Us',
  GET_QUOTE: 'Get Quote',
  BOOK_TRAVEL: 'Book Now',
  DOWNLOAD: 'Download',
  INSTALL_MOBILE_APP: 'Install App',
  USE_APP: 'Open App',
  WHATSAPP_MESSAGE: 'WhatsApp',
  OPEN_LINK: 'Open Link',
};

export default function CreativesList() {
  const nav = useNavigate();
  const [creatives, setCreatives] = useState<FbCreative[] | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('ALL');

  // Retry repeats whatever failed — a forced live pull or a cached read.
  const lastForce = useRef(false);
  const load = async (force = false) => {
    lastForce.current = force;
    setRefreshing(true);
    setError(null);
    try {
      const r = await facebookAdCreativesService.list(force);
      setCreatives(r.data);
      setFetchedAt(r.fetchedAt);
      setFromCache(r.fromCache);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load creatives');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const filtered = (creatives ?? []).filter((c) => {
    if (kindFilter !== 'ALL' && c.media_kind !== kindFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        (c.headline ?? '').toLowerCase().includes(q) ||
        (c.message ?? '').toLowerCase().includes(q) ||
        c.id.includes(q)
      );
    }
    return true;
  });

  const filtersActive = !!search.trim() || kindFilter !== 'ALL';

  const cacheDesc =
    fromCache && fetchedAt
      ? `Cached · last fetch ${relativeTime(fetchedAt)}`
      : undefined;

  return (
    <>
      <Topbar
        title="Ad Creatives"
        breadcrumb="Facebook Ads / Ad Creatives"
        description={cacheDesc}
        right={
          <div className="flex items-center gap-2">
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:text-text-primary disabled:opacity-50 transition-colors"
              onClick={() => load(true)}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <Link to="/facebook/creatives/new">
              <Button iconLeft={<Plus size={14} />}>New Creative</Button>
            </Link>
          </div>
        }
      />

      <div className="px-7 py-7 w-full space-y-5">
        {/* A failed refresh over an already-loaded grid; a failed first load shows in place of the grid. */}
        {error && creatives !== null && (
          <div className="flex items-start gap-3 rounded-xl border border-danger/30 bg-danger-soft p-4">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-danger" />
            <p className="flex-1 text-sm text-danger">{error}</p>
            <Button size="sm" variant="secondary" onClick={() => load(lastForce.current)} disabled={refreshing}>Retry</Button>
          </div>
        )}

        {/* Search + filter row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-72">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search creatives…"
              iconLeft={<Search size={14} />}
            />
          </div>
          <div className="flex items-center gap-1.5">
            {(['ALL', 'image', 'video'] as KindFilter[]).map((k) => (
              <button
                key={k}
                onClick={() => setKindFilter(k)}
                className={clsx(
                  'h-8 rounded-lg px-3 text-xs font-semibold transition-colors',
                  kindFilter === k
                    ? 'bg-gold-gradient text-on-primary shadow-soft'
                    : 'border border-border bg-surface text-text-muted hover:bg-surface-alt hover:text-text-primary',
                )}
              >
                {k === 'ALL' ? 'All' : k.charAt(0).toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {creatives === null && error ? (
          <LoadErrorState what="your creatives" message={error} onRetry={() => load(lastForce.current)} />
        ) : creatives === null ? (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton h-72 rounded-2xl" />
            ))}
          </div>
        ) : filtered.length === 0 && filtersActive ? (
          <NoMatchesState onClear={() => { setSearch(''); setKindFilter('ALL'); }} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<ImageIcon size={28} />}
            title="No creatives yet"
            description="Creatives hold the image or video, text and call to action of an ad. Build one here and reuse it across your campaigns."
            action={
              <Link to="/facebook/creatives/new">
                <Button iconLeft={<Plus size={14} />}>Create your first creative</Button>
              </Link>
            }
          />
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c) => (
              <CreativeCard key={c.id} creative={c} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function CreativeCard({ creative: c }: { creative: FbCreative }) {
  const KindIcon = KIND_ICON[c.media_kind];
  // Prefer ImageKit mirror — Meta's signed thumbnail/image URLs expire
  // after a few hours; backend rehosts on first sync.
  const previewSrc = c.imagekit_url || c.thumbnail_url || c.image_url;

  return (
    <div
      className={clsx(
        'group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-soft',
        'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-elevated hover:border-primary/60',
      )}
    >
      {/* Media preview — 16:9 */}
      <div className="relative aspect-video w-full overflow-hidden bg-surface-alt">
        {previewSrc ? (
          <>
            <img
              src={previewSrc}
              alt={c.name}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
            />
            {c.media_kind === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white shadow-lg">
                  <Play size={20} className="ml-0.5" />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-text-muted">
              <KindIcon size={32} />
              <span className="text-xs">{KIND_LABELS[c.media_kind]} creative</span>
            </div>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="font-semibold text-text-primary leading-tight">{c.name}</div>
        {c.headline && (
          <div className="truncate text-sm text-text-secondary">{c.headline}</div>
        )}
        <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-2">
          <span
            className={clsx(
              'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide',
              c.media_kind === 'image' && 'bg-info-soft text-info',
              c.media_kind === 'video' && 'bg-warning-soft text-warning',
              c.media_kind === 'carousel' && 'bg-success-soft text-success',
              c.media_kind === 'unknown' && 'bg-surface-alt text-text-muted',
            )}
          >
            <KindIcon size={10} />
            {KIND_LABELS[c.media_kind]}
          </span>
          {c.cta && (
            <span className="inline-flex items-center rounded-md bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary uppercase tracking-wide">
              {CTA_LABEL[c.cta] ?? c.cta}
            </span>
          )}
          {c.link && (
            <a
              href={c.link}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="ml-auto flex items-center gap-1 text-2xs text-text-muted hover:text-primary"
            >
              <ExternalLink size={10} />
              {truncateUrl(c.link)}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function truncateUrl(url: string): string {
  try { return new URL(url).hostname; } catch { return url.slice(0, 20); }
}
